import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { forwardRef } from 'react';
import { TextField as MuiTextField } from '@material-ui/core';
import cn from 'classnames';
import { ChevronDown } from '../../icons';
import { useBiAction } from '../BiTracking';
import { BottomSheetContentLayout } from '../BottomSheet';
import { useDevice } from '../Device';
import { useOverlayContext } from '../OverlayContext';
import { Tooltip } from '../Tooltip';
import { skinToColor, useInlineSelectStyles } from './InlineSelect.styles';
function InlineSelectBottomSheet(props) {
    const { children, value, onChange, isSelected } = props;
    const { hideBottomSheet } = useOverlayContext();
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        children: React.Children.map(children, (child)=>{
            if (!/*#__PURE__*/ React.isValidElement(child)) return child;
            const childProps = child.props;
            return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, childProps), {
                selected: isSelected ? isSelected(childProps.value, value) : childProps.value === value,
                onClick: ()=>{
                    onChange === null || onChange === void 0 ? void 0 : onChange({
                        target: {
                            value: childProps.value
                        }
                    });
                    hideBottomSheet();
                }
            }));
        })
    });
}
// See note on BaseTextField — forwardRef + MUI types collapses BiProps' discriminated union.
const InlineSelectImpl = /*#__PURE__*/ forwardRef(function InlineSelect(props, ref) {
    var _props_InputProps;
    const { children, className, required, testId, inputProps, menuMaxHeight = 300, menuMaxWidth, skin = 'standard', SelectProps, helperText, value, onChange, isSelected, bi, noBi: _noBi } = props, rest = _object_without_properties(props, [
        "children",
        "className",
        "required",
        "testId",
        "inputProps",
        "menuMaxHeight",
        "menuMaxWidth",
        "skin",
        "SelectProps",
        "helperText",
        "value",
        "onChange",
        "isSelected",
        "bi",
        "noBi"
    ]);
    const logBiAction = useBiAction();
    const { isMobileDevice } = useDevice();
    const handleChange = (e)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'inline_select',
                entityName: bi.name,
                actionType: 'select',
                testId: testId !== null && testId !== void 0 ? testId : '',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    selected_value: e.target.value
                })
            });
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(e);
    };
    const { pushBottomSheetPage } = useOverlayContext();
    const overrides = useInlineSelectStyles({
        menuMaxHeight,
        menuMaxWidth,
        color: skinToColor[skin],
        isMobileDevice
    });
    const mobileSelectProps = isMobileDevice ? {
        open: false,
        onOpen: openMobileSheet
    } : {};
    function openMobileSheet() {
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(InlineSelectBottomSheet, {
                value: value,
                onChange: handleChange,
                isSelected: isSelected,
                children: children
            })
        });
    }
    const textField = /*#__PURE__*/ _jsx(MuiTextField, _object_spread_props(_object_spread({
        innerRef: ref,
        value: value,
        onChange: handleChange
    }, rest), {
        "data-testid": testId,
        select: true,
        className: cn(className, overrides.root),
        variant: "standard",
        size: "small",
        helperText: undefined,
        InputProps: {
            ref: (_props_InputProps = props.InputProps) === null || _props_InputProps === void 0 ? void 0 : _props_InputProps.ref,
            required,
            classes: {
                root: overrides.inputRoot,
                input: overrides.inputInput
            }
        },
        SelectProps: _object_spread({
            IconComponent: (param)=>{
                let { className } = param;
                return /*#__PURE__*/ _jsx(ChevronDown, {
                    size: 16,
                    className: cn(className, overrides.icon)
                });
            },
            MenuProps: {
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                },
                getContentAnchorEl: null,
                classes: {
                    paper: overrides.menu
                },
                ['data-testid']: testId && "".concat(testId, "-backdrop")
            },
            SelectDisplayProps: {
                ['data-testid']: testId && "".concat(testId, "-select")
            }
        }, SelectProps, mobileSelectProps),
        inputProps: _object_spread({
            ['data-testid']: testId && "".concat(testId, "-input")
        }, inputProps),
        children: children
    }));
    return helperText ? /*#__PURE__*/ _jsx(Tooltip, {
        placement: "top",
        title: helperText,
        testId: testId && "".concat(testId, "-tooltip"),
        children: textField
    }) : textField;
});
export const InlineSelect = InlineSelectImpl;
